import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useProductContext } from './product-provider';

interface DisplayModeContextProps {
  isMobileWeb: boolean;
  isBreakpointSm: boolean;
  isMobileView: boolean;
}

const defaultValues: DisplayModeContextProps = {
  isMobileWeb: false,
  isBreakpointSm: false,
  isMobileView: false,
};

const DisplayModeContext = createContext<DisplayModeContextProps | null>(null);

export const useDisplayModeContext = () => {
  const context = useContext(DisplayModeContext);
  return context || defaultValues;
};

const isPlatformMobileDevice = () => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return false;
  }
  const isMSStreamAvailable = 'MSStream' in window;
  return (
    !isMSStreamAvailable &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      /Android/.test(navigator.userAgent))
  );
};

export const DisplayModeProvider = ({
  children,
}: PropsWithChildren<unknown>) => {
  const { productName } = useProductContext();
  const isWebProduct =
    productName === 'web' || productName === 'paperpal-lifesciences-for-web';
  // Start from the SSR-safe defaults (all false) so the first client render
  // matches the server markup and never triggers a hydration mismatch. The
  // effect below computes the real values synchronously right after mount.
  const [isMobileWeb, setIsMobileWeb] = useState(
    typeof window !== 'undefined' && isWebProduct && window.innerWidth <= 768,
  );
  const [isBreakpointSm, setIsBreakpointSm] = useState(false);
  // isMobileView is used to determine if the current view is mobile or not,
  // based on the window width. This variable will be used specifically for
  // mobile view handling, while `isMobileWeb` is planned to be deprecated soon.
  // Note: this is different from `isMobileWeb`, which also takes into account
  // the product type and user agent.
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' && isWebProduct && window.innerWidth <= 767,
  );

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }
    const smallScreenQuery = window.matchMedia('(max-width: 768px)');
    const mobileViewQuery = window.matchMedia('(max-width: 767px)');

    const checkIsMobile = () => {
      const isSmallScreen =
        smallScreenQuery.matches || window.innerWidth <= 767;
      const isMobileViewport =
        mobileViewQuery.matches || window.innerWidth <= 768;
      const isPlatformMobile = isPlatformMobileDevice();

      setIsBreakpointSm(isSmallScreen);
      setIsMobileView(isWebProduct && isMobileViewport);
      setIsMobileWeb(isWebProduct && (isSmallScreen || isPlatformMobile));
    };

    checkIsMobile();
    // Re-check on the next frame to catch cases where the viewport width has
    // not fully settled by the time this effect first runs on mobile.
    const rafId = window.requestAnimationFrame(checkIsMobile);

    smallScreenQuery.addEventListener('change', checkIsMobile);
    mobileViewQuery.addEventListener('change', checkIsMobile);
    window.addEventListener('orientationchange', checkIsMobile);
    window.addEventListener('pageshow', checkIsMobile);
    window.addEventListener('resize', checkIsMobile);

    return () => {
      window.cancelAnimationFrame(rafId);
      smallScreenQuery.removeEventListener('change', checkIsMobile);
      mobileViewQuery.removeEventListener('change', checkIsMobile);
      window.removeEventListener('orientationchange', checkIsMobile);
      window.removeEventListener('pageshow', checkIsMobile);
      window.removeEventListener('resize', checkIsMobile);
    };
  }, [isWebProduct]);

  return (
    <DisplayModeContext.Provider
      value={{ isMobileWeb, isBreakpointSm, isMobileView }}
    >
      {children}
    </DisplayModeContext.Provider>
  );
};
